import type { QueryOptions } from "@tanstack/react-query"

import type { ScopeKeyParameter } from "@wagmi/core/internal"
import {
  type SimulateEVCBatchCallErrorType,
  type SimulateEVCBatchCallParameters,
  type SimulateEVCBatchCallReturnType,
  simulateEVCBatchCall,
} from "../actions/simulateEVCBatchCall"
import type { TransactionItems } from "../factories"
import type { ExtendedConfig, UnionPartial } from "../types"
import { filterQueryOptions } from "../utils"

export type SimulateEVCBatchCallOptions<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined,
> = UnionPartial<
  SimulateEVCBatchCallParameters<config, transactions, chainId>
> &
  ScopeKeyParameter

export function simulateEVCBatchCallQueryOptions<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined,
>(
  config: config,
  options: SimulateEVCBatchCallOptions<
    config,
    transactions,
    chainId
  > = {} as any,
) {
  return {
    async queryFn({ queryKey }) {
      const { transactions, connector } = options
      if (!transactions?.length) throw new Error("transactions are required")
      const { scopeKey: _, ...parameters } = queryKey[1]
      return simulateEVCBatchCall(config, {
        transactions,
        connector,
        ...(parameters as any),
      })
    },
    queryKey: simulateEVCBatchCallQueryKey(options),
  } as const satisfies QueryOptions<
    SimulateEVCBatchCallQueryFnData<config, chainId>,
    SimulateEVCBatchCallErrorType,
    SimulateEVCBatchCallData<config, chainId>,
    SimulateEVCBatchCallQueryKey<config, transactions, chainId>
  >
}

export type SimulateEVCBatchCallQueryFnData<
  config extends ExtendedConfig,
  chainId extends config["chains"][number]["id"] | undefined,
> = SimulateEVCBatchCallReturnType<config, chainId>

export type SimulateEVCBatchCallData<
  config extends ExtendedConfig,
  chainId extends config["chains"][number]["id"] | undefined,
> = SimulateEVCBatchCallQueryFnData<config, chainId>

export function simulateEVCBatchCallQueryKey<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined,
>(
  options: SimulateEVCBatchCallOptions<
    config,
    transactions,
    chainId
  > = {} as any,
) {
  const { connector: _c, value, ...rest } = options
  return [
    "simulateEVCBatchCall",
    filterQueryOptions({ ...rest, value: value?.toString() }),
  ] as const
}

export type SimulateEVCBatchCallQueryKey<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined,
> = ReturnType<
  typeof simulateEVCBatchCallQueryKey<config, transactions, chainId>
>
